import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import Login from './components/Login';
import UnitSelect from './components/UnitSelect';
import Dashboard from './components/Dashboard';
import Lancamento from './components/Lancamento';
import Historico from './components/Historico';
import AlterarSenha from './components/AlterarSenha';

const UNIT_LABELS = { sinop: 'Sinop', cv: 'Campo Verde' };
const UNIT_COLORS = { sinop: { bg: '#e8f1fb', color: '#185FA5' }, cv: { bg: '#eaf3de', color: '#1a5c38' } };

export default function App() {
  const [user, setUser]           = useState(null);
  const [profile, setProfile]     = useState(null);
  const [unit, setUnit]           = useState(null);
  const [tab, setTab]             = useState('dash');
  const [lancamentos, setLancamentos] = useState([]);
  const [carteira, setCarteira]   = useState({ bomb20: 0, bomb5: 0, ibc1000: 0, granel: 0 });
  const [loading, setLoading]     = useState(true);
  const [editingDate, setEditingDate] = useState(null);
  const [showAlterarSenha, setShowAlterarSenha] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        supabase.from('profiles').select('*').eq('id', session.user.id).single()
          .then(({ data }) => {
            if (data) {
              setUser(session.user);
              setProfile(data);
              if (data.perfil !== 'admin') setUnit(data.perfil);
            }
          });
      }
      setLoading(false);
    });
  }, []);

  const loadData = useCallback(async (u) => {
    const [{ data: lancs }, { data: cart }] = await Promise.all([
      supabase.from('lancamentos').select('*').eq('unidade', u).order('data', { ascending: true }),
      supabase.from('carteira').select('*').eq('unidade', u).single(),
    ]);
    setLancamentos((lancs || []).map(l => ({ ...l.dados, data: l.data, id: l.id })));
    if (cart) setCarteira({ bomb20: cart.bomb20, bomb5: cart.bomb5, ibc1000: cart.ibc1000, granel: cart.granel });
  }, []);

  useEffect(() => {
    if (unit) loadData(unit);
  }, [unit, loadData]);

  const handleLogin = (u, p) => {
    setUser(u); setProfile(p);
    if (p.perfil !== 'admin') setUnit(p.perfil);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null); setProfile(null); setUnit(null);
    setLancamentos([]); setTab('dash'); setEditingDate(null);
  };

  const handleDelete = async (data) => {
    await supabase.from('lancamentos')
      .delete()
      .eq('unidade', unit)
      .eq('data', data);
    await loadData(unit);
  };

  // Recalcula saldos iniciais em cascata a partir de uma data
  const recalcularCascata = async (dataInicio, unitRef) => {
    const u = unitRef || unit;

    const { data: base } = await supabase
      .from('lancamentos').select('*')
      .eq('unidade', u).eq('data', dataInicio).single();
    if (!base) return;

    const { data: lancs } = await supabase
      .from('lancamentos').select('*')
      .eq('unidade', u).gt('data', dataInicio)
      .order('data', { ascending: true });
    if (!lancs || lancs.length === 0) return;

    let prev = base.dados;

    for (const lanc of lancs) {
      const d = lanc.dados;
      const novosDados = { ...d };

      // Polietileno
      if (prev.pe && d.pe) {
        const sf = (prev.pe.saldo_ini||0) + (prev.pe.entrada||0) - (prev.pe.consumo||0);
        novosDados.pe = { ...d.pe, saldo_ini: sf };
      }

      // Aditivo
      if (prev.aditivo && d.aditivo) {
        const sf = (prev.aditivo.saldo_ini||0) + (prev.aditivo.entrada||0) - (prev.aditivo.consumo||0);
        novosDados.aditivo = { ...d.aditivo, saldo_ini: sf };
      }

      // Ureia por fornecedor
      if (prev.ureia && d.ureia) {
        const novaUreia = { ...d.ureia };
        Object.keys(d.ureia).forEach(fid => {
          const pu = prev.ureia[fid];
          if (pu) {
            const sf = (pu.saldo_ini||0) + (pu.entrada||0) - (pu.consumo||0);
            novaUreia[fid] = { ...d.ureia[fid], saldo_ini: sf };
          }
        });
        novosDados.ureia = novaUreia;
      }

      // Embalagens
      if (prev.emb && d.emb) {
        const novaEmb = { ...d.emb };
        Object.keys(d.emb).forEach(eid => {
          const pe = prev.emb[eid];
          if (pe) {
            const sf = (pe.saldo_ini||0) + (pe.entrada||0) - (pe.saida||0);
            novaEmb[eid] = { ...d.emb[eid], saldo_ini: sf };
          }
        });
        novosDados.emb = novaEmb;
      }

      // Arla
      if (prev.arla && d.arla) {
        const novaArla = { ...d.arla };
        Object.keys(d.arla).forEach(aid => {
          const pa = prev.arla[aid];
          if (pa) {
            const sf = (pa.saldo_ini||0) + (pa.producao||0) - (pa.saida||0);
            novaArla[aid] = { ...d.arla[aid], saldo_ini: sf };
          }
        });
        novosDados.arla = novaArla;
      }

      await supabase.from('lancamentos').update({ dados: novosDados })
        .eq('unidade', u).eq('data', lanc.data);

      prev = novosDados;
    }
  };

  const handleSave = async (dados, cart, dateOverride) => {
    const today = dateOverride || new Date().toISOString().split('T')[0];

    await supabase.from('lancamentos').upsert(
      { unidade: unit, data: today, dados, criado_por: user.id },
      { onConflict: 'unidade,data' }
    );
    await supabase.from('carteira').update({
      bomb20: cart.bomb20, bomb5: cart.bomb5, ibc1000: cart.ibc1000, granel: cart.granel,
      atualizado_em: new Date().toISOString(),
    }).eq('unidade', unit);

    // Recalcula em cascata os dias posteriores
    await recalcularCascata(today);

    // Dispara e-mail (apenas lançamentos novos, não edições do admin)
    if (!editingDate && profile.perfil !== 'admin') {
      try {
        await supabase.functions.invoke('enviar-email', {
          body: { unidade: unit, data: today, dados, carteira: cart, usuario: profile.nome }
        });
      } catch (e) {
        console.error('Erro ao enviar e-mail:', e);
      }
    }

    setEditingDate(null);
    await loadData(unit);
    setTab('dash');
  };

  if (loading) return <div style={styles.loading}>Carregando...</div>;
  if (!user || !profile) return <Login onLogin={handleLogin} />;
  if (profile.perfil === 'admin' && !unit) {
    return <UnitSelect profile={profile} onSelect={setUnit} onLogout={handleLogout} />;
  }

  const isAdmin = profile.perfil === 'admin';
  const uc = UNIT_COLORS[unit];

  return (
    <div style={styles.app}>
      {showAlterarSenha && (
        <AlterarSenha profile={profile} onClose={() => setShowAlterarSenha(false)} />
      )}
      <div style={styles.topbar}>
        <div style={styles.topbarLeft}>
          <img src="/logo.png" alt="Plantivo" style={styles.logoImg} />
          <span style={styles.appName}>Gestão Arla</span>
          <span style={{ ...styles.pill, background: uc.bg, color: uc.color }}>{UNIT_LABELS[unit]}</span>
          {isAdmin && <span style={styles.pillAdmin}>Admin</span>}
        </div>
        <div style={styles.topbarRight}>
          <span style={styles.dateStr}>
            {new Date().toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
          {isAdmin && (
            <button style={styles.btnSm} onClick={() => { setUnit(null); setTab('dash'); }}>
              Trocar unidade
            </button>
          )}
          <button style={styles.btnSm} onClick={() => setShowAlterarSenha(true)}>Alterar senha</button>
          <button style={styles.btnSm} onClick={handleLogout}>Sair</button>
        </div>
      </div>

      <div style={styles.tabs}>
        {['dash', 'lancamento', 'historico'].map((t, i) => (
          <button key={t}
            style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
            onClick={() => { setTab(t); setEditingDate(null); }}>
            {['Dashboard', 'Lançamento diário', 'Histórico'][i]}
          </button>
        ))}
      </div>

      <div style={styles.content}>
        {tab === 'dash' && (
          <Dashboard lancamentos={lancamentos} carteira={carteira} unit={unit} />
        )}
        {tab === 'lancamento' && (
          <Lancamento
            lancamentos={lancamentos}
            carteira={carteira}
            unit={unit}
            isAdmin={isAdmin}
            onSave={handleSave}
            editingDate={editingDate}
            onCancelEdit={() => { setEditingDate(null); setTab('historico'); }}
          />
        )}
        {tab === 'historico' && (
          <Historico
            lancamentos={lancamentos}
            unit={unit}
            isAdmin={isAdmin}
            onEdit={(date) => { setEditingDate(date); setTab('lancamento'); }}
            onDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
}

const styles = {
  loading:    { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#888', fontFamily: '"DM Sans", sans-serif', background: '#f5f4f0' },
  app:        { minHeight: '100vh', background: '#f5f4f0', fontFamily: '"DM Sans", sans-serif' },
  topbar:     { background: '#fff', borderBottom: '0.5px solid #e0ddd6', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
  topbarLeft:  { display: 'flex', alignItems: 'center', gap: 10 },
  topbarRight: { display: 'flex', alignItems: 'center', gap: 8 },
  logoImg:    { height: 28, width: 'auto', objectFit: 'contain' },
  appName:    { fontSize: 16, fontWeight: 600, color: '#1a1a18' },
  pill:       { fontSize: 11, padding: '2px 10px', borderRadius: 20, fontWeight: 500 },
  pillAdmin:  { fontSize: 11, padding: '2px 10px', borderRadius: 20, fontWeight: 500, background: '#eeedfe', color: '#534AB7' },
  dateStr:    { fontSize: 12, color: '#aaa' },
  btnSm:      { background: '#fff', border: '0.5px solid #e0ddd6', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', color: '#555', fontFamily: '"DM Sans", sans-serif' },
  tabs:       { display: 'flex', gap: 0, padding: '0 24px', background: '#fff', borderBottom: '0.5px solid #e0ddd6' },
  tab:        { padding: '10px 18px', cursor: 'pointer', fontSize: 13, border: 'none', background: 'none', color: '#aaa', borderBottom: '2px solid transparent', marginBottom: -1, fontFamily: '"DM Sans", sans-serif', fontWeight: 400 },
  tabActive:  { color: '#1a1a18', borderBottomColor: '#1a5c38', fontWeight: 500 },
  content:    { padding: 24, maxWidth: 1200, margin: '0 auto' },
};
